import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Empty } from 'antd';
import { FormHeader } from './common/FormHeader';
import { SectionRenderer } from './common/SectionRenderer';
import { createQuestionRenderer } from './common/QuestionRenderers';
import { isQuestionEnabled, isSectionEnabled } from './utils/previewHelpers';
import PatientInfoCard from '../clinical/common/PatientInfoCard';

/**
 * FormPreview - Renders form schema preview in OpenMRS style
 * Supports both uncontrolled (internal state) and controlled (external state) modes
 * 
 * @param {Object} schema - Form schema
 * @param {Object} initialValues - Initial form values (optional, for controlled mode)
 * @param {Function} onValuesChange - Callback when values change (optional, for controlled mode)
 * @param {boolean} readonly - Whether form is readonly
 * @param {boolean} compact - Use compact styling
 * @param {string} formName - Form name to display in header
 * @param {boolean} showHeader - Whether to show header with icon and form name
 * @param {Function} onLoadExternalForm - Callback for loading external forms
 * @param {Object} loadedExternalForms - Map of loaded external forms
 */
const FormPreview = ({ 
  schema, 
  initialValues = {}, 
  onValuesChange,
  readonly = false,
  compact = false,
  formName,
  showHeader = true,
  patientFullName,
  patientDateOfBirth,
  patientYearOfBirth,
  patientId,
  // For loading external forms dynamically
  onLoadExternalForm,
  loadedExternalForms = {}
}) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [activePageTab, setActivePageTab] = useState('0');
  const [externalForms, setExternalForms] = useState(loadedExternalForms);

  // Track what was last applied from parent to avoid re-syncing our own updates
  const lastAppliedInitialRef = useRef(initialValues);
  const prevFormValuesRef = useRef(formValues);

  // Sync with external initial values only when they genuinely differ
  // from what we last applied (prevents parent echo-back from retriggering)
  useEffect(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) return;

    const serialized = JSON.stringify(initialValues);
    if (serialized !== JSON.stringify(lastAppliedInitialRef.current)) {
      lastAppliedInitialRef.current = initialValues;
      prevFormValuesRef.current = initialValues;
      setFormValues(initialValues);
    }
  }, [initialValues]);

  // Notify parent of changes (controlled mode)
  useEffect(() => {
    const hasChanged = JSON.stringify(prevFormValuesRef.current) !== JSON.stringify(formValues);
    
    if (hasChanged && onValuesChange) {
      prevFormValuesRef.current = formValues;
      onValuesChange(formValues);
    }
  }, [formValues]); // intentionally omit onValuesChange to prevent loop

  // Clear values for hidden sections and questions (for optimal JSON storage).
  // Uses a functional updater so the effect only depends on schema, not formValues,
  // which prevents a setState→effect→setState infinite loop.
  const cleanupTimerRef = useRef(null);
  useEffect(() => {
    if (!schema?.pages) return;

    // Debounce to batch rapid value changes and avoid cascading updates
    clearTimeout(cleanupTimerRef.current);
    cleanupTimerRef.current = setTimeout(() => {
      setFormValues(prev => {
        const keysToRemove = new Set();

        schema.pages.forEach(page => {
          (page.sections || []).forEach(section => {
            const sectionVisible = isSectionEnabled(section, schema, prev);

            if (!sectionVisible) {
              (section.questions || []).forEach(question => {
                if (prev[question.id] !== undefined) {
                  keysToRemove.add(question.id);
                }
              });
            } else {
              (section.questions || []).forEach(question => {
                const questionVisible = isQuestionEnabled(question, prev, schema);
                if (!questionVisible && prev[question.id] !== undefined) {
                  keysToRemove.add(question.id);
                }
              });
            }
          });
        });

        if (keysToRemove.size === 0) return prev;

        const updated = { ...prev };
        keysToRemove.forEach(key => delete updated[key]);
        return updated;
      });
    }, 100);

    return () => clearTimeout(cleanupTimerRef.current);
  }, [schema, formValues]);

  // Helper to get child IDs for an answer from schema (matches QuestionRenderers.js logic)
  const getChildIds = React.useCallback((questionId, answerId) => {
    if (!schema?.pages) return [];
    
    const childIds = [];
    
    const collectChildIds = (children, keyPrefix) => {
      if (!children?.length) return;
      children.forEach((child, ci) => {
        const childId = child.id || child.concept || child.conceptCode || `${keyPrefix}_child_${ci}`;
        childIds.push(childId);
        if (child.children?.length) {
          collectChildIds(child.children, childId);
        }
      });
    };
    
    for (const page of schema.pages) {
      for (const section of (page.sections || [])) {
        for (const question of (section.questions || [])) {
          if (question.id === questionId) {
            const answers = question.questionOptions?.answers || [];
            const answer = answers.find((a, idx) => {
              const aId = a.concept || a.conceptCode || `answer_${idx}`;
              return aId === answerId;
            });
            if (answer?.children) {
              collectChildIds(answer.children, answerId);
            }
            return childIds;
          }
        }
      }
    }
    return childIds;
  }, [schema]);

  const handleCheckboxChange = React.useCallback((questionId, answerId, checked) => {
    setFormValues(prev => {
      const newQuestionValues = {
        ...(prev[questionId] || {}),
        [answerId]: checked
      };
      
      // If unchecking, also clear all children values
      if (checked === false || (typeof checked === 'object' && !checked)) {
        const childIds = getChildIds(questionId, answerId);
        childIds.forEach(childId => {
          delete newQuestionValues[childId];
        });
      }
      
      return {
        ...prev,
        [questionId]: newQuestionValues
      };
    });
  }, [getChildIds]);

  const handleInputChange = React.useCallback((questionId, value) => {
    setFormValues(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  // Create question renderer with all dependencies
  // For external forms, we recursively render FormPreview
  const renderExternalForm = React.useCallback((externalSchema, externalInitialValues, externalOnValuesChange) => {
    // Use the same component recursively
    return (
      <FormPreview
        schema={externalSchema}
        initialValues={externalInitialValues || {}}
        onValuesChange={externalOnValuesChange}
        readonly={readonly}
        compact={true}
        showHeader={false}
        onLoadExternalForm={onLoadExternalForm}
        loadedExternalForms={externalForms}
      />
    );
  }, [readonly, onLoadExternalForm, externalForms]);

  const renderQuestion = React.useMemo(() => {
    if (!schema) return () => null;
    return createQuestionRenderer(
      formValues,
      handleInputChange,
      handleCheckboxChange,
      readonly,
      compact,
      isQuestionEnabled,
      externalForms,
      setExternalForms,
      onLoadExternalForm,
      renderExternalForm,
      schema
    );
  }, [schema, formValues, handleInputChange, handleCheckboxChange, readonly, compact, externalForms, renderExternalForm, onLoadExternalForm]);

  if (!schema) {
    return (
      <div style={{ padding: compact ? 8 : 12 }}>
        <Empty description="Select a form template to begin" />
      </div>
    );
  }

  const renderSection = (section, sectionIndex) => (
    <SectionRenderer
      key={sectionIndex}
      section={section}
      sectionIndex={sectionIndex}
      renderQuestion={renderQuestion}
      compact={compact}
      schema={schema}
      formValues={formValues}
    />
  );

  const tabItems = schema.pages?.map((page, pageIndex) => ({
    key: String(pageIndex),
    label: page.label,
    children: (
      <div style={{ padding: compact ? '4px 0' : '8px 0' }}>
        {/* Page-level free text (outside sections) */}
        {page.freeText && (
          <div 
            style={{ 
              marginBottom: 12,
              padding: 8,
              backgroundColor: 'transparent'
            }}
            dangerouslySetInnerHTML={{ __html: page.freeText }}
          />
        )}
        {page.sections?.map((section, sectionIndex) => 
          renderSection(section, sectionIndex)
        )}
      </div>
    )
  })) || [];

  // Single page - no tabs needed
  if (tabItems.length <= 1) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flexShrink: 0 }}>
          <FormHeader
            formName={formName}
            schema={schema}
            showHeader={showHeader}
            compact={compact}
          />
          <PatientInfoCard
            patientFullName={patientFullName}
            patientDateOfBirth={patientDateOfBirth}
            patientYearOfBirth={patientYearOfBirth}
            showCard={true}
            style={{ marginBottom: 24 }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {tabItems[0]?.children || <Empty description="No form content" />}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .form-preview-tabs-container .ant-tabs {
          display: flex;
          flex-direction: column;
          height: 100%;
          flex: 1;
        }
        .form-preview-tabs-container .ant-tabs-content-holder {
          flex: 1;
          overflow: hidden;
          min-height: 0;
        }
        .form-preview-tabs-container .ant-tabs-content {
          height: 100%;
        }
        .form-preview-tabs-container .ant-tabs-tabpane {
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
        }
      `}</style>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flexShrink: 0 }}>
          <FormHeader
            formName={formName}
            schema={schema}
            showHeader={showHeader}
            compact={compact}
          />
          <PatientInfoCard
            patientFullName={patientFullName}
            patientDateOfBirth={patientDateOfBirth}
            patientYearOfBirth={patientYearOfBirth}
            showCard={true}
            style={{ marginBottom: 24 }}
          />
        </div>
        <div className="form-preview-tabs-container" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Tabs
            activeKey={activePageTab}
            onChange={setActivePageTab}
            items={tabItems.map(item => ({
              ...item,
              children: (
                <div style={{ padding: compact ? '4px 0' : '8px 0' }}>
                  {item.children}
                </div>
              )
            }))}
            size={compact ? 'small' : 'middle'}
            style={{ 
              padding: compact ? 0 : '0 8px',
              height: '100%'
            }}
            tabBarStyle={{ flexShrink: 0 }}
          />
        </div>
      </div>
    </>
  );
};

export default FormPreview;
